import React, { useEffect } from 'react';
import { DragAndDrop } from '@progress/kendo-react-common';

import { DroppableList } from './DroppableList';
import { DraggableListItem } from './DraggableListItem';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from '../../store';
import { WidgetsState } from '../../Interfaces/Overview/WidgetsState';
import { Dispatch } from 'redux';
import { saveWidgetsState } from '../../store/UserSettings/UserSettingsActions';
import { GetString } from '../StringResourceManager';

export const DragContext = React.createContext<
  [
    null | { element: React.RefObject<HTMLElement | null>; data: WidgetsState },
    Function
  ]
>([
  null,
  () => {
    /* noop */
  },
]);
export const DropContext = React.createContext<
  [
    null | { element: React.RefObject<HTMLElement | null>; data: WidgetsState },
    Function
  ]
>([
  null,
  () => {
    /* noop */
  },
]);
export const DirectionContext = React.createContext<
  ['before' | 'after', Function]
>([
  'before',
  () => {
    /* noop */
  },
]);

const inRange = (min: number, max: number, index: number) => {
  return Math.max(min, Math.min(max, index));
};

export const WidgetsDroppableList = () => {
  const dropTimeout = React.useRef<number>();
  const dispatch: Dispatch<any> = useDispatch();
  const widgetsState: WidgetsState[] = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.widgetsState
  );

  const [data, setData] = React.useState<WidgetsState[]>(widgetsState);
  const [drag, setDrag] = React.useState<{
    element: React.RefObject<HTMLElement | null>;
    data: WidgetsState;
  } | null>(null);
  const [drop, setDrop] = React.useState<{
    element: React.RefObject<HTMLElement | null>;
    data: WidgetsState;
  } | null>(null);

  const [direction, setDirection] = React.useState<'before' | 'after'>(
    'before'
  );

  useEffect(() => {
    return (): void => {
      if (dropTimeout.current) clearTimeout(dropTimeout.current);
    };
  }, []);

  const handleDropChange = React.useCallback(
    (
      newDrop: {
        element: React.RefObject<HTMLElement | null>;
        data: WidgetsState;
      } | null
    ) => {
      if (!newDrop) {
        dropTimeout.current = window.setTimeout(() => {
          setDrop(newDrop);
        }, 50);
      } else {
        clearTimeout(dropTimeout.current);
        setDrop(newDrop);
      }
    },
    []
  );

  const handleItemDrop = React.useCallback(() => {
    if (!drag || !drop) {
      return;
    }
    const newData = data.slice();
    const oldIndex = newData.findIndex(d => d.id === drag.data.id);
    const dataItem = newData[oldIndex];

    if (dataItem) {
      newData.splice(oldIndex, 1);

      const newIndex = inRange(
        0,
        newData.length,
        newData.findIndex(d => d.id === drop.data.id) +
          (direction === 'after' ? 1 : 0)
      );

      newData.splice(newIndex, 0, dataItem);

      saveWidgetsState(dispatch, newData);

      setData(newData);
    }
  }, [data, direction, drag, drop]);

  const handleListDrop = React.useCallback(() => {
    if (!drag || !drop) {
      return;
    }
    const newData = data.slice();
    const oldIndex = newData.findIndex(d => d.id === drag.data.id);
    const dataItem = newData[oldIndex];

    if (dataItem) {
      newData.splice(newData.length - 1, 0, newData.splice(oldIndex, 1)[0]);
      setData(newData);
    }
  }, [data, drag, drop]);

  var getActiveWidgets = () => {
    var filteredData = [...data];
    for (let i = 0; i < filteredData.length; ++i) {
      filteredData[i].name = GetString(filteredData[i].nameKey);
    }
    return filteredData.filter(i => i.visible);
  };

  var activeWidgets: WidgetsState[] = getActiveWidgets();

  return (
    <div style={{ display: 'flex' }}>
      <DragContext.Provider value={[drag, setDrag]}>
        <DropContext.Provider value={[drop, handleDropChange]}>
          <DirectionContext.Provider value={[direction, setDirection]}>
            <DragAndDrop>
              <div style={{ flexBasis: '45%' }}>
                {data !== undefined && data.length > 0 ? (
                  <DroppableList onDrop={handleListDrop}>
                    {activeWidgets.map(item => (
                      <DraggableListItem
                        onDrop={handleItemDrop}
                        key={item.id}
                        data={item}
                      >
                        {item.name}
                      </DraggableListItem>
                    ))}
                  </DroppableList>
                ) : null}
              </div>
            </DragAndDrop>
          </DirectionContext.Provider>
        </DropContext.Provider>
      </DragContext.Provider>
    </div>
  );
};
